import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, Save, Image as ImageIcon } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { resolveImageUrl } from "@/lib/images";

const FIELDS = [
  { name: "practice_name", label: "Practice / organization name" },
  { name: "specialty", label: "Specialty / focus" },
  { name: "address", label: "Address" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "zip", label: "ZIP" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Public email" },
  { name: "website", label: "Website" },
  { name: "hours", label: "Hours of operation" },
  { name: "insurance_accepted", label: "Insurance accepted" },
];

const TABS = ["profile", "gallery", "blog"];

export default function ManageListingPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [tab, setTab] = useState("profile");
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  const refresh = async () => {
    const r = await api.get(`/listings/${id}`);
    setListing(r.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (tab === "blog") {
      api.get("/blog", { params: { listing_id: id, page_size: 50 } }).then((r) => setPosts(r.data.results));
    }
  }, [tab, id]);

  if (!listing) return <div className="container-page py-16">Loading…</div>;

  const save = async (updates) => {
    try {
      const { data } = await api.patch(`/listings/${id}`, updates);
      setListing(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="container-page py-10" data-testid="manage-listing-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </Link>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="chip">Manage</span>
          <h1 className="mt-3 font-display text-4xl font-bold text-primary">{listing.practice_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{listing.category} {listing.city ? `· ${listing.city}` : ""}</p>
        </div>
        <Link to={`/listing/${id}`} className="btn-secondary !py-2 text-sm">Public view</Link>
      </div>

      <div className="mt-8 flex gap-2 border-b border-border" data-testid="manage-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`relative px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${tab === t ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
            data-testid={`tab-${t}`}
          >
            {t}
            {tab === t && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-accent" />}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <ProfileTab listing={listing} onSave={save} />
      )}
      {tab === "gallery" && (
        <GalleryTab listing={listing} onChange={refresh} />
      )}
      {tab === "blog" && (
        <BlogTab listingId={id} posts={posts} onCreate={() => navigate(`/dashboard/listings/${id}/blog/new`)} />
      )}
    </div>
  );
}

function ProfileTab({ listing, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      practice_name: listing.practice_name || "",
      specialty: listing.specialty || "",
      description: listing.description || "",
      address: listing.address || "",
      city: listing.city || "",
      state: listing.state || "",
      zip: listing.zip || "",
      phone: listing.phone || "",
      email: listing.email || "",
      website: listing.website || "",
      hours: listing.hours || "",
      insurance_accepted: listing.insurance_accepted || "",
      accepts_medicaid: !!listing.accepts_medicaid,
      accepts_medicare: !!listing.accepts_medicare,
      services: listing.services || [],
      hero_url: listing.hero_url || "",
    });
  }, [listing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const heroPreview = form.hero_url ? resolveImageUrl(form.hero_url) : null;

  return (
    <form onSubmit={submit} className="mt-8 grid max-w-3xl gap-5" data-testid="profile-tab-form">
      <div>
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Hero image</span>
        <div className="flex items-center gap-4">
          <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-md border border-border bg-muted">
            {heroPreview ? <img src={heroPreview} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
          </div>
          <ImageUpload label="Upload hero" testId="hero-upload" onUploaded={(url) => set("hero_url", url)} />
          {form.hero_url && (
            <button type="button" onClick={() => set("hero_url", "")} className="text-sm text-muted-foreground hover:text-destructive">Remove</button>
          )}
        </div>
      </div>

      {FIELDS.map((f) => (
        <label key={f.name} className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">{f.label}</span>
          <input
            value={form[f.name] || ""}
            onChange={(e) => set(f.name, e.target.value)}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
            data-testid={`profile-${f.name}`}
          />
        </label>
      ))}

      <label className="block">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Description</span>
        <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent" data-testid="profile-description" />
      </label>

      <div>
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Services (one per line)</span>
        <textarea
          rows={4}
          value={(form.services || []).join("\n")}
          onChange={(e) => set("services", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          className="w-full rounded-md border border-border bg-card px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-accent"
          data-testid="profile-services"
        />
      </div>

      <div className="flex flex-wrap items-center gap-5">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.accepts_medicaid} onChange={(e) => set("accepts_medicaid", e.target.checked)} className="h-4 w-4 accent-accent" data-testid="profile-medicaid" />
          Accepts Medicaid
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.accepts_medicare} onChange={(e) => set("accepts_medicare", e.target.checked)} className="h-4 w-4 accent-accent" data-testid="profile-medicare" />
          Accepts Medicare
        </label>
      </div>

      <button type="submit" className="btn-primary w-fit" data-testid="profile-save">
        <Save className="h-4 w-4" /> Save changes
      </button>
    </form>
  );
}

function GalleryTab({ listing, onChange }) {
  const gallery = listing.gallery_urls || [];

  const add = async (url) => {
    await api.post(`/listings/${listing.id}/gallery`, { url });
    toast.success("Photo added");
    onChange();
  };
  const remove = async (url) => {
    await api.delete(`/listings/${listing.id}/gallery`, { data: { url } });
    onChange();
  };

  return (
    <div className="mt-8" data-testid="gallery-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-primary">Gallery ({gallery.length})</h3>
        <ImageUpload label="Add photo" testId="gallery-upload" onUploaded={(url) => add(url)} />
      </div>
      {gallery.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No photos yet — upload your first.</p>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {gallery.map((url) => (
            <div key={url} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted" data-testid={`gallery-item-${url.slice(-8)}`}>
              <img src={resolveImageUrl(url)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => remove(url)} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-md bg-white/90 text-destructive opacity-0 transition-opacity group-hover:opacity-100">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogTab({ listingId, posts, onCreate }) {
  return (
    <div className="mt-8" data-testid="blog-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-primary">Blog posts</h3>
        <button onClick={onCreate} className="btn-accent !py-2 text-sm" data-testid="new-post">
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>
      {posts.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No posts yet — share an update with your community.</p>
      ) : (
        <ul className="mt-6 grid gap-3">
          {posts.map((p) => (
            <li key={p.id} className="card-surface flex items-center justify-between gap-4 p-4" data-testid={`post-row-${p.id}`}>
              <div className="min-w-0 flex-1">
                <Link to={`/blog/${p.slug}`} className="font-display text-lg font-bold text-primary hover:text-accent line-clamp-1">{p.title}</Link>
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.published ? "Published" : "Draft"} · {new Date(p.published_at).toLocaleDateString()} · {p.view_count || 0} views
                </p>
              </div>
              <Link to={`/dashboard/listings/${listingId}/blog/edit/${p.id}`} className="btn-secondary !py-2 text-sm">Edit</Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
